"use client";

import { useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import {
  trackInteraction,
  trackGeneric,
  type InteractableTypeInput,
} from "@/app/lib/tracking/interactions";

/**
 * Impression tracking hook'u.
 *
 * Bir bölüm (section/kategori/marka vb.) viewport'a girdiğinde BİR KEZ
 * `trackInteraction(type, id, "view")` gönderir. Kullanıcının gerçekten
 * gördüğü içeriği ölçer — click'ten farklı olarak pasif görüntülenmeyi sayar.
 *
 * - `triggerOnce`: tek atım; scroll sırasında tekrar tetiklenmez (INP dostu).
 * - `threshold: 0.25`: bölümün ~%25'i görününce say — hızlı geçişleri saymaz.
 * - Geçersiz id (0/null/undefined) → hiç event atılmaz.
 *
 * Dönen `ref`'i bölümün kök elementine bağla:
 *   const trackRef = useTrackView("section", section.id);
 *   return <section ref={trackRef}>...</section>;
 */
export function useTrackView(
  type: InteractableTypeInput,
  id?: number | string | null,
): (node?: Element | null) => void {
  const fired = useRef(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.25,
  });

  useEffect(() => {
    if (fired.current || !inView) return;
    const numericId = typeof id === "string" ? Number(id) : id;
    if (typeof numericId !== "number" || !Number.isFinite(numericId) || numericId <= 0) {
      return;
    }
    fired.current = true;
    trackInteraction(type, numericId, "view");
  }, [inView, type, id]);

  return ref;
}

/**
 * Model bazlı olmayan (numeric id'siz) bölümler için impression tracking.
 *
 * Bölüm viewport'a girince BİR KEZ `trackGeneric(type, "view")` gönderir.
 * Ürün detayındaki öneri carousel'leri, yorumlar/bilgi blokları gibi
 * scroll-derinliği/engagement sinyalleri için — id yok, tip string olarak kaydedilir.
 *
 *   const ref = useTrackGenericView("product_reviews_section");
 *   return <section ref={ref}>...</section>;
 */
export function useTrackGenericView(
  type: string,
): (node?: Element | null) => void {
  const fired = useRef(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.25,
  });

  useEffect(() => {
    if (fired.current || !inView || !type) return;
    fired.current = true;
    trackGeneric(type, "view");
  }, [inView, type]);

  return ref;
}
